// src/utils/saveManager.js
import {
  IMMEDIATE_SAVE_EVENTS,
  DEBOUNCED_SAVE_EVENTS,
  getDebounceDelay,
  createSaveEventData
} from './saveEvents';

class SaveManager {
  constructor() {
    this.debounceTimers = new Map();
    this.saveQueue = [];
    this.currentSavePromise = null;
    this.isOnline = navigator.onLine;
    this.retryAttempts = new Map();
    this.maxRetries = 3;

    // Listen for online/offline events
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.processSaveQueue();
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
    });
  }

    // Helper method to generate a session ID
  generateSessionId() {
    return 'sess_' + Math.random().toString(36).substring(2, 9);
  }

  // Helper methid to calculate character changes
  calculateCharacterChanges(newText, oldText) {

    // Simple character difference calculation
    if (!oldText) return newText ? newText.length : 0;
    return Math.abs((newText || '').length - oldText.length);
  }

  // Main save handler
  async handleSave(eventType, erdState, username, customData = {}, getClassContext = null) {
    if (!username || !eventType) {
      console.warn('SaveManager: Missing username or eventType');
      return false;
    }

    // BUG FIX 1: Use current erdState environment, not version control selection
    // The environment should come from erdState.selectedEnvironment which reflects
    // the actual environment being worked on, not the version control dropdown
    const actualEnvironment = erdState.selectedEnvironment || 'None';

    // Prevent saves when environment is 'None'
    if (actualEnvironment === 'None') {
      console.log('SaveManager: Skipping save - environment is set to None');
      return false;
    }

    const saveData = createSaveEventData(eventType, {
      ...customData,
      inputText: customData.inputText || erdState.inputText, // Use updated text if provided
      selectedEnvironment: actualEnvironment, // Use actual environment from ERD state
      erdData: customData.erdData || erdState.erdData, // Use passed data if available
      svgContent: customData.svgContent || erdState.svgContent, // Use passed data if available
      relationships: customData.relationships || erdState.relationships, // Use passed data if available
      feedback: erdState.feedback,
      qa: erdState.qa,
      rating: erdState.rating,
      comment: erdState.comment,
      feedbackId: erdState.feedbackId,
      textareaHeight: erdState.textareaHeight,
      leftPanelWidth: erdState.leftPanelWidth,
      editorMode: erdState.editorMode
    });

    // Handle immediate saves
    if (IMMEDIATE_SAVE_EVENTS.includes(eventType)) {
      return await this.executeSave(username, saveData, getClassContext);
    }

    // Handle debounced saves
    if (DEBOUNCED_SAVE_EVENTS.includes(eventType)) {
      return this.debouncedSave(eventType, username, saveData, getClassContext);
    }

    console.warn(`SaveManager: Unknown event type: ${eventType}`);
    return false;
  }

  // Debounced save implementation
  debouncedSave(eventType, username, saveData, getClassContext) {
    const delay = getDebounceDelay(eventType);
    const timerId = `${username}_${eventType}`;

    // Clear existing timer for this event type
    if (this.debounceTimers.has(timerId)) {
      clearTimeout(this.debounceTimers.get(timerId));
    }

    // Set new timer
    const timer = setTimeout(async () => {
      this.debounceTimers.delete(timerId);
      await this.executeSave(username, saveData, getClassContext);
    }, delay);

    this.debounceTimers.set(timerId, timer);
    return true;
  }

  // Execute the actual save operation
  async executeSave(username, saveData, getClassContext) {
    try {
      // BUG FIX 2: Use the environment from saveData, not from external source
      // Generate filename with timestamp and correct environment
      const timestamp = new Date().toISOString()
        .replace(/:/g, '')
        .replace(/-/g, '')
        .replace(/\..+/, '')
        .replace('T', '-');

      // Use the environment from the actual ERD state being saved
      const environment = saveData.selectedEnvironment || 'none';
      const filename = `${timestamp}-${environment.toLowerCase()}.json`;

      // Debug logging for SVG data
      console.log('SaveManager: SVG data available:', {
        hasFullSvgContent: !!saveData.fullSvgContent,
        hasSvgContent: !!saveData.svgContent,
        hasOriginalErdData: !!saveData.originalErdData,
        hasErdData: !!saveData.erdData
      });

      // Prepare the complete work state with correct metadata
      const workState = {
        metadata: {
          username,
          environment, // This should match the actual working environment
          timestamp: saveData.timestamp,
          eventType: saveData.eventType,
          source: saveData.source,
          version: '1.0'
        },
        erdState: {
          inputText: saveData.inputText || '',
          selectedEnvironment: saveData.selectedEnvironment || '', // Current working environment
          selectedRelationship: 'All',
          erdData: saveData.originalErdData || saveData.erdData || null,
          svgContent: saveData.fullSvgContent || saveData.svgContent || null,
          relationships: saveData.relationships || [],
          feedback: saveData.feedback || [],
          qa: saveData.qa || [],
          rating: saveData.rating || 5,
          comment: saveData.comment || '',
          feedbackId: saveData.feedbackId || null,
          textareaHeight: saveData.textareaHeight || 256,
          leftPanelWidth: saveData.leftPanelWidth || 60,
          editorMode: saveData.editorMode || 'raw'
        }
      };

      // If offline, add to queue
      if (!this.isOnline) {
        this.saveQueue.push({ username, filename, workState, saveData, getClassContext });
        this.saveToLocalStorage(username, workState);
        return true;
      }

      // Execute the save
      const success = await this.saveToServer(username, filename, workState, getClassContext);

      if (success) {
        // Also save to localStorage as backup
        this.saveToLocalStorage(username, workState);
        this.retryAttempts.delete(`${username}_${filename}`);
        return true;
      } else {
        // If server save fails, still save locally
        this.saveToLocalStorage(username, workState);
        this.addToRetryQueue(username, filename, workState, saveData, getClassContext);
        return false;
      }

    } catch (error) {
      console.error('SaveManager: Error in executeSave:', error);
      return false;
    }
  }

  // Save to server
  async saveToServer(username, filename, workState, getClassContext) {
    try {
      // Get class context if available
      const classContext = getClassContext ? getClassContext() : null;

      // Build headers
      const headers = {
        'Content-Type': 'application/json',
        'User-Role': 'Student',
        'Requesting-User': username
      };

      // Add class context headers if available
      if (classContext && classContext.class_id) {
        headers['Class-ID'] = classContext.class_id.toString();
      }
      if (classContext && classContext.class_code) {
        headers['Class-Code'] = classContext.class_code;
      }

      // TODO(human): Update the request body to match the new saved_works API schema
      const response = await fetch('/api/saved-works', {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          // Basic identification
          username: username,
          sessionId: workState.metadata
            ?.sessionId || this.generateSessionId(),

          // Metadata for class context and environment
          metadata: {
            classId: classContext?.class_id || null,
            environment: workState.erdState.selectedEnvironment,
            environmentName: `Environment:
            ${workState.erdState.selectedEnvironment}`,
            eventType: workState.metadata.eventType,
            source: workState.metadata.source,
            autoSave: workState.metadata.eventType === 'auto_save' ? {
              interval_seconds: 30,
              character_changes:
                this.calculateCharacterChanges(),
              last_keystroke: new Date().toISOString()
            } : {}
          },

          // ERD state following the saved_works schema
          erdState: {
            inputText: workState.erdState.inputText,
            editorMode: workState.erdState.editorMode,

            // Visual editor state (if available)
            visualEditor: workState.erdState.visualEditor || {},

            // UI state mapping
            uiState: {
              leftPanelWidth: workState.erdState.leftPanelWidth,
              selectedRelationship: workState.erdState.selectedRelationship || 'All',
              textareaHeight: workState.textareaHeight
            },

            // Feedback and content
            feedback: {
              feedbackId: workState.erdState.feedbackId,
              score: workState.erdState.rating,
              lastFeedbackText: Array.isArray(workState.erdState.feedback) ? workState.erdState.feedback.join(' ') : workState.erdState.feedback,
              qaCount: Array.isArray(workState.erdState.qa) ? workState.erdState.qa.length : 0
            },

            svgContent: workState.erdState.svgContent
          }
        })
      });

      if (response.ok) {
        const result = await response.json();
        console.log(`[SaveManager] Successfully saved work with ID: ${result.work_id},version: ${result.version}`);
        return result.success;
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('SaveManager: Server returned error:', response.status, errorData);
        return false;
      }
    } catch (error) {
      console.error('SaveManager: Network error during save:', error);
      return false;
    }
  }

  // Save to localStorage as backup
  saveToLocalStorage(username, workState) {
    try {
      const key = `erd_work_state_${username}`;
      localStorage.setItem(key, JSON.stringify(workState.erdState));

      // Also save with timestamp for version history
      const timestampKey = `erd_backup_${username}_${Date.now()}`;
      localStorage.setItem(timestampKey, JSON.stringify(workState));

      // Clean up old backups (keep only last 5)
      this.cleanupLocalBackups(username);
    } catch (error) {
      console.error('SaveManager: Error saving to localStorage:', error);
    }
  }

  // Clean up old localStorage backups
  cleanupLocalBackups(username) {
    try {
      const prefix = `erd_backup_${username}_`;
      const keys = Object.keys(localStorage)
        .filter(key => key.startsWith(prefix))
        .sort()
        .reverse();

      // Remove all but the latest 5 backups
      if (keys.length > 5) {
        keys.slice(5).forEach(key => {
          localStorage.removeItem(key);
        });
      }
    } catch (error) {
      console.error('SaveManager: Error cleaning up localStorage:', error);
    }
  }

  // Add to retry queue for failed saves
  addToRetryQueue(username, filename, workState, saveData, getClassContext) {
    const retryKey = `${username}_${filename}`;
    const currentAttempts = this.retryAttempts.get(retryKey) || 0;

    if (currentAttempts < this.maxRetries) {
      this.retryAttempts.set(retryKey, currentAttempts + 1);
      this.saveQueue.push({ username, filename, workState, saveData, isRetry: true, getClassContext });
    } else {
      console.error(`SaveManager: Max retry attempts reached for ${retryKey}`);
      this.retryAttempts.delete(retryKey);
    }
  }

  // Process queued saves when back online
  async processSaveQueue() {
    if (!this.isOnline || this.saveQueue.length === 0 || this.currentSavePromise) {
      return;
    }

    this.currentSavePromise = this.processQueueItems();
    await this.currentSavePromise;
    this.currentSavePromise = null;
  }

  // Process individual queue items
  async processQueueItems() {
    while (this.saveQueue.length > 0 && this.isOnline) {
      const item = this.saveQueue.shift();
      try {
        await this.saveToServer(item.username, item.filename, item.workState, item.getClassContext);
        // Small delay between saves to avoid overwhelming the server
        await new Promise(resolve => setTimeout(resolve, 100));
      } catch (error) {
        console.error('SaveManager: Error processing queue item:', error);
        // Re-add to queue if it's not a retry or hasn't exceeded max retries
        if (!item.isRetry) {
          this.addToRetryQueue(item.username, item.filename, item.workState, item.saveData, item.getClassContext);
        }
      }
    }
  }

  // Clear all timers (useful for cleanup)
  clearAllTimers() {
    this.debounceTimers.forEach(timer => clearTimeout(timer));
    this.debounceTimers.clear();
  }

  // Get save queue status
  getQueueStatus() {
    return {
      queueLength: this.saveQueue.length,
      activeTimers: this.debounceTimers.size,
      isOnline: this.isOnline,
      isProcessing: !!this.currentSavePromise
    };
  }
}

// Create singleton instance
const saveManager = new SaveManager();

export default saveManager;